import _ from 'lodash'
import { MenuItem } from './menu-item'
import { MenuVendorCategory } from './menu-vendor'
import type { VendorMenuCategoryHeroAsset } from '@/generated/graphql'

export type MenuCategory = {
  id: string
  name: string
  items: MenuItem[]
  description?: string | null
  position?: number | null
  itemCount?: number
  hasMoreItems?: boolean
  isFeatured?: boolean
  heroAssets?: VendorMenuCategoryHeroAsset[]
}

export const createMenuCategoriesOptimized = (
  baselineItems: MenuItem[],
  filteredItems: MenuItem[],
  vendorCategories: MenuVendorCategory[],
): MenuCategory[] => {
  const vendorCategoryIdSet = new Set(vendorCategories.map((vc) => vc.id))

  const baselineCategoryIds = new Set<string>()
  for (const item of baselineItems) {
    const categoryIds = item.categoryIdArray?.filter((catId) =>
      vendorCategoryIdSet.has(catId.toString()),
    )
    if (categoryIds && categoryIds.length > 0) {
      for (const catId of categoryIds) {
        baselineCategoryIds.add(catId.toString())
      }
    } else {
      baselineCategoryIds.add('everything')
    }
  }

  const itemsByCategory = new Map<string, Map<string, MenuItem>>()

  for (const item of filteredItems) {
    const itemKey = item._id || item.variationId
    if (!itemKey) continue

    const categoryIds = item.categoryIdArray?.filter((catId) =>
      vendorCategoryIdSet.has(catId.toString()),
    )

    const targetCategories =
      categoryIds && categoryIds.length > 0
        ? categoryIds.map((id) => id.toString())
        : ['everything']

    for (const catId of targetCategories) {
      let categoryItems = itemsByCategory.get(catId)
      if (!categoryItems) {
        categoryItems = new Map<string, MenuItem>()
        itemsByCategory.set(catId, categoryItems)
      }
      if (!categoryItems.has(itemKey)) {
        categoryItems.set(itemKey, item)
      }
    }
  }

  const allCategories: Array<{ id: string; name: string; position: number }> = [
    ...vendorCategories.map((vc) => ({
      id: vc.id,
      name: vc.name,
      position: vc.position ?? Number.MAX_SAFE_INTEGER,
    })),
    { id: 'everything', name: 'Everything Else', position: 9999 },
  ]

  allCategories.sort((a, b) => a.position - b.position)

  const result: MenuCategory[] = []
  for (const category of allCategories) {
    if (!baselineCategoryIds.has(category.id)) continue

    const categoryItemsMap = itemsByCategory.get(category.id)
    const items = categoryItemsMap ? Array.from(categoryItemsMap.values()) : []

    result.push({
      id: category.id,
      name: category.name,
      items,
    })
  }

  return result
}

export const createMenuCategories = (
  baselineItems: MenuItem[],
  filteredItems: MenuItem[],
  vendorCategories: MenuVendorCategory[],
): MenuCategory[] => {
  const vendorCategoryIds = vendorCategories.map((vc) => vc.id)

  const baselineCategoriesWithItems = _.chain(baselineItems)
    .flatMap((item) => {
      let categoryIds: (string | number)[] =
        item.categoryIdArray?.filter((categoryId) =>
          vendorCategoryIds.includes(categoryId.toString()),
        ) || []
      if (categoryIds.length <= 0) {
        categoryIds = ['everything']
      }
      return categoryIds.map((categoryId) => categoryId?.toString())
    })
    .uniq()
    .value()

  const itemsByCategory = _.chain(filteredItems)
    .flatMap((item) => {
      let categoryIds: (string | number)[] =
        item.categoryIdArray?.filter((categoryId) =>
          vendorCategoryIds.includes(categoryId.toString()),
        ) || []
      if (categoryIds.length <= 0) {
        categoryIds = ['everything']
      }

      return categoryIds.map((categoryId) => ({
        categoryId: categoryId?.toString(),
        item,
      }))
    })
    .groupBy('categoryId')
    .mapValues((groupedItems) => ({
      items: groupedItems.map((gi) => gi.item),
    }))
    .value()
  return _.chain([
    ...vendorCategories,
    {
      id: 'everything',
      name: 'Everything Else',
      position: 9999,
    },
  ])
    .sortBy(({ position }) => position)
    .filter(({ id }) => {
      return baselineCategoriesWithItems.includes(id)
    })
    .map(({ id, name }) => ({
      id,
      name,
      items: _.uniqBy(
        itemsByCategory[id]?.items ?? [],
        (menuItem) => menuItem._id || menuItem.variationId,
      ),
    }))
    .value()
}
